import { useEffect, useState } from 'react';

/**
 * Snapshot da bateria do device, lido via Battery Status API.
 *
 * Suporte:
 *  - Chrome/Edge Android + desktop: ✅ implementado
 *  - Safari iOS / macOS: ❌ removido por motivos de privacidade
 *  - Firefox: ❌ removido
 *
 * Quando não suportado, retorna valores neutros (não-baixos) — o sistema
 * fallback nesse caso é só hardware tier + rede.
 */
export interface BatteryStatus {
  /** 0.0 a 1.0. undefined se a API não tá disponível. */
  level?: number;
  /** Carregando agora? undefined se API não disponível. */
  charging?: boolean;
  /**
   * Sinaliza que o app deve estar em "modo leve" pelo critério bateria.
   * True quando: NÃO carregando E level < 0.2 (20%).
   * Quando carregando, ignora — usuário tá plugando, animações podem rolar.
   * Quando API não disponível, retorna false (não force modo leve à toa).
   */
  isLowBattery: boolean;
}

interface BatteryManager extends EventTarget {
  level: number;
  charging: boolean;
  chargingTime: number;
  dischargingTime: number;
}

interface NavigatorWithBattery extends Navigator {
  getBattery?: () => Promise<BatteryManager>;
}

const LOW_THRESHOLD = 0.2; // 20%

/**
 * Hook que retorna o estado da bateria. Re-renderiza só quando muda.
 *
 * Em devices sem suporte (Safari), retorna `isLowBattery: false` sempre.
 * Não tenta polling ou outras heurísticas — preferimos não restringir
 * funcionalidades à toa em iPhones modernos (que são a maioria do nosso
 * uso) só porque não dá pra ler bateria.
 */
export function useBatteryStatus(): BatteryStatus {
  const [status, setStatus] = useState<BatteryStatus>({ isLowBattery: false });

  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    const nav = navigator as NavigatorWithBattery;
    if (typeof nav.getBattery !== 'function') return;

    let battery: BatteryManager | undefined;
    let cancelled = false;

    const update = () => {
      if (!battery || cancelled) return;
      const isLowBattery = !battery.charging && battery.level < LOW_THRESHOLD;
      setStatus({
        level: battery.level,
        charging: battery.charging,
        isLowBattery,
      });
    };

    nav
      .getBattery()
      .then((b) => {
        if (cancelled) return;
        battery = b;
        update();
        b.addEventListener('levelchange', update);
        b.addEventListener('chargingchange', update);
      })
      .catch(() => {
        // Permission negada / API gated por gesture / etc. Mantém valor neutro.
      });

    return () => {
      cancelled = true;
      battery?.removeEventListener('levelchange', update);
      battery?.removeEventListener('chargingchange', update);
    };
  }, []);

  return status;
}
